import React, {memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import classNames from "classnames";
import styles from "./styles.module.scss";
import {TextMedium16} from "@/app/components/texts";
import Space from "@/app/components/space";
import {debounce} from "@/app/utils/custom_function";
import CustomCssTransition from "@/app/components/custom_css_transition/widget";
import LazyAssets from "@/app/utils/assets_widget";

interface InputProps {
  label?: string;
  prefixIcon?: ReactNode;
  hint?: string;
  value?: string;
  className?: string;
  error?: string;
  rounded?: boolean;
  buttonInInput?: ReactNode;
  onChange?: (value: string) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  isPassword?: boolean;
  isTextArea?: boolean;
  isWhite?: boolean;
  limit?: number;
  onClick?: () => void
}

const Input: React.FC<InputProps> = ({
                                       label,
                                       prefixIcon,
                                       hint,
                                       value,
                                       className,
                                       error,
                                       rounded,
                                       buttonInInput,
                                       onChange,
                                       onKeyDown,
                                       isPassword = false,
                                       isTextArea = false,
                                       isWhite = false,
                                       limit,
                                       onClick
                                     }) => {
  const [textValue, setTextValue] = useState(value ?? "");
  const [passwordValue, setPasswordValue] = useState(value ?? "");
  const [showPassword, setShowPassword] = useState(!isPassword);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const debouncedOnChange = useMemo(
    () => (onChange ? debounce(onChange, 500) : undefined),
    [onChange]
  );

  useEffect(() => {
    setTextValue(value ?? "");
    setPasswordValue(value ?? "");
  }, [value]);

  useEffect(() => {
    const val = isPassword ? passwordValue : textValue;
    debouncedOnChange?.(val);
  }, [debouncedOnChange, textValue, passwordValue, isPassword]);

  useEffect(() => {
    if (isTextArea && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [textValue, isTextArea]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const val = e.target.value;
      if (isPassword) {
        setPasswordValue(val);
      } else {
        setTextValue(val);
      }
    },
    [isPassword]
  );

  const handleBeforeInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const ev = e.nativeEvent as InputEvent;
      if (ev.inputType === "insertText" && ev.data != null) {
        setPasswordValue(prev =>
          limit != null
            ? (prev + ev.data).slice(0, limit)
            : prev + ev.data
        );
        e.preventDefault();
      }
    },
    [limit]
  );

  const handleKeyDownPassword = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        setPasswordValue(prev => prev.slice(0, -1));
        e.preventDefault();
      } else {
        onKeyDown?.(e);
      }
    },
    [onKeyDown]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const paste = e.clipboardData.getData("text");
      setPasswordValue(prev =>
        limit != null
          ? (prev + paste).slice(0, limit)
          : prev + paste
      );
      e.preventDefault();
    },
    [limit]
  );

  const handlePasswordFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const len = passwordValue.length;
      e.currentTarget.setSelectionRange(len, len);
    },
    [passwordValue]
  );

  const displayValue = isPassword
    ? showPassword
      ? passwordValue
      : "#".repeat(passwordValue.length)
    : textValue;

  return (
    <div
      className={classNames(
        styles.inputBlock,
        {[styles.error]: !!error},
        className
      )}
    >
      {label && (
        <>
          <TextMedium16 text={label}/>
          <Space.H10/>
        </>
      )}

      <div
        className={classNames(styles.inputWrapper, {
          [styles.rounded]: rounded,
          [styles.hasButtonInInput]: !!buttonInInput,
          [styles.white]: isWhite,
          [styles.chat]: !!buttonInInput && isTextArea
        })}
        onClick={() => {
          if (isTextArea) textareaRef.current?.focus();
          else inputRef.current?.focus();
          onClick?.();
        }}
      >
        {prefixIcon}
        {prefixIcon && <Space.W16/>}
        {isTextArea ? (
          <textarea
            ref={textareaRef}
            className={classNames(styles.input, styles.textarea)}
            value={displayValue}
            onChange={handleChange}
            placeholder={hint}
            onKeyDown={onKeyDown}
            maxLength={limit}
            rows={1}
            style={{overflow: "hidden", resize: "none"}}
          />
        ) : isPassword && !showPassword ? (
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={displayValue}
            placeholder={hint}
            onInput={handleBeforeInput}
            onKeyDown={handleKeyDownPassword}
            onPaste={handlePaste}
            onFocus={handlePasswordFocus}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={displayValue}
            placeholder={hint}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            maxLength={limit}
          />
        )}

        {buttonInInput}
        {isPassword && (
          showPassword ?
            <LazyAssets.UnShow onClick={() => setShowPassword((v) => false)} className={styles.iconShowPassword}/> :
            <LazyAssets.Show onClick={() => setShowPassword((v) => true)} className={styles.iconShowPassword}/>
        )}
      </div>

      {limit != null && (
        <>
          <Space.H10/>
          <div className={styles.counter}>
            <TextMedium16 text={`${displayValue.length}/${limit}`}/>
          </div>
        </>
      )}

      {error && (
        <CustomCssTransition bool={!error}>
          <Space.H10/>
          <TextMedium16 text={error}/>
        </CustomCssTransition>
      )}
    </div>
  );
};

export default memo(Input);